import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utility/axiosInstance";

export interface RelationalDatabase {
    id: number;
    name: string;
    engine: "mysql" | "mariadb";
    db_name: string;
    db_user: string;
    db_version: string;
    status: "provisioning" | "ready" | "error";
    floating_ip: string | null;
    port: number;
    instance_id: string;
    created_at: string;
    access_point?: string | null;
    private_ip?: string | null;
    // shown once on creation only
    db_password?: string;
}

interface RelationalDatabasesState {
    databases: RelationalDatabase[];
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
}

const initialState: RelationalDatabasesState = { databases: [], status: "idle", error: null };

export const fetchRelationalDatabases = createAsyncThunk("relationalDatabases/fetch", async () => {
    const res = await axiosInstance.get("/v1/relational-databases");
    return res.data as RelationalDatabase[];
});

export const deleteRelationalDatabase = createAsyncThunk("relationalDatabases/delete", async (id: number) => {
    await axiosInstance.delete(`/v1/relational-databases/${id}`);
    return id;
});

const relationalDatabasesSlice = createSlice({
    name: "relationalDatabases",
    initialState,
    reducers: {
        addRelationalDatabase: (state, action) => { state.databases.unshift(action.payload); },
        updateRelationalDatabaseStatus: (state, action) => {
            const db = state.databases.find(d => d.id === action.payload.id);
            if (db) { db.status = action.payload.status; db.floating_ip = action.payload.floating_ip ?? db.floating_ip; }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRelationalDatabases.pending,   (s) => { s.status = "loading"; })
            .addCase(fetchRelationalDatabases.fulfilled, (s, a) => { s.status = "succeeded"; s.databases = a.payload; })
            .addCase(fetchRelationalDatabases.rejected,  (s, a) => { s.status = "failed"; s.error = a.error.message ?? null; })
            .addCase(deleteRelationalDatabase.fulfilled, (s, a) => { s.databases = s.databases.filter(d => d.id !== a.payload); });
    },
});

export const { addRelationalDatabase, updateRelationalDatabaseStatus } = relationalDatabasesSlice.actions;
export default relationalDatabasesSlice.reducer;
